import { computed, inject, Injectable, signal } from '@angular/core';
import { ApiService } from '../../shared/services/api.service';
import { Observable, of, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

export interface DeclarationCheckResult {
    required: boolean;
    reason?: string;
    declaration_id?: number;
    tickets_restaurant_enabled?: boolean;
    today?: string;
    prefillData?: {
        morning?: string;
        afternoon?: string;
        ticketRestaurant?: boolean;
        comment?: string;
    };
}

export interface DayDeclaration {
    date: string;
    morning: string;
    afternoon: string;
    ticketRestaurant: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class DailyDeclarationService {
    private apiService = inject(ApiService);

    private readonly API_URL = 'api/sirh/configurations/';

    // Signals
    private _checkResult = signal<DeclarationCheckResult | null>(null);
    private _isLoading = signal<boolean>(false);
    private _dismissedToday = signal<boolean>(false);

    // Public readonly signals
    readonly checkResult = this._checkResult.asReadonly();
    readonly isLoading = this._isLoading.asReadonly();
    readonly dismissedToday = this._dismissedToday.asReadonly();

    // Computed signal to determine if modal should be shown
    readonly shouldShowModal = computed(() => {
        const result = this._checkResult();
        const dismissed = this._dismissedToday();
        return result?.required === true && !dismissed;
    });

    /**
     * Check if daily declaration is required for the current user.
     * Returns an Observable with the result.
     */
    checkDeclarationRequired(): Observable<DeclarationCheckResult> {
        this._isLoading.set(true);

        return this.apiService.get<DeclarationCheckResult>(
            `${this.API_URL}check_declaration_required/`,
            {},
            true // salt to bypass cache
        ).pipe(
            tap((result: DeclarationCheckResult) => {
                this._checkResult.set(result);
                this._isLoading.set(false);
            }),
            catchError(error => {
                console.error('Error checking declaration requirement:', error);
                this._isLoading.set(false);
                const errorResult: DeclarationCheckResult = {
                    required: false,
                    reason: 'Erreur lors de la verification'
                };
                this._checkResult.set(errorResult);
                return of(errorResult);
            })
        );
    }

    /**
     * Dismiss the modal for today (stored in session).
     */
    dismissForToday(): void {
        this._dismissedToday.set(true);
        // Store in sessionStorage so it persists for the session
        const today = new Date().toISOString().split('T')[0];
        sessionStorage.setItem('sirh_declaration_dismissed', today);
    }

    /**
     * Check if already dismissed today (from session storage).
     */
    checkDismissedToday(): boolean {
        const dismissedDate = sessionStorage.getItem('sirh_declaration_dismissed');
        const today = new Date().toISOString().split('T')[0];
        const isDismissed = dismissedDate === today;
        this._dismissedToday.set(isDismissed);
        return isDismissed;
    }

    /**
     * Reset the check result and dismissed state.
     */
    reset(): void {
        this._checkResult.set(null);
        this._dismissedToday.set(false);
        sessionStorage.removeItem('sirh_declaration_dismissed');
    }
}
